<script setup lang="ts">
import GeneratedPersonSearchTable from "./GeneratedPersonSearchTable.vue";
import PersonAddGuestPrivsDialog from "./PersonAddGuestPrivsDialog.vue";
import { useMembershipService } from "../../../cbi-generator/frontend/composables/useMembershipFrontendService";
import type { MembershipStaffSearchParams } from "../../../cbi-generator/generated/dto/MembershipStaffSearchParams";
import type { MembershipStaffSearchResult } from "../../../cbi-generator/generated/dto/MembershipStaffSearchResult";
import { MembershipStaffSearchParamsMeta } from "../../../cbi-generator/generated/dto/MembershipStaffSearchParamsMeta";
import { MembershipStaffSearchResultMeta } from "../../../cbi-generator/generated/dto/MembershipStaffSearchResultMeta";
import type { MembershipMemberSearchParams } from "../../../cbi-generator/generated/dto/MembershipMemberSearchParams";
import type { MembershipMemberSearchResult } from "../../../cbi-generator/generated/dto/MembershipMemberSearchResult";
import { MembershipMemberSearchParamsMeta } from "../../../cbi-generator/generated/dto/MembershipMemberSearchParamsMeta";
import { MembershipMemberSearchResultMeta } from "../../../cbi-generator/generated/dto/MembershipMemberSearchResultMeta";
import { PurchasableState } from "../../../cbi-generator/generated/dto/PurchasableState";
import type { GeneratedTableConfig } from "@/composables/generated/contracts";
import { emptySearchParams } from "@/composables/useTableHelper";
import { useUtility } from "@/composables/useUtility";

const props = withDefaults(
  defineProps<{ personId: number; mode?: "staff" | "member" }>(),
  { mode: "staff" },
);
type Row = MembershipStaffSearchResult | MembershipMemberSearchResult;
const service = useMembershipService();
const { formatDateNY, formatCost } = useUtility();
const staffConfig: GeneratedTableConfig<
  MembershipStaffSearchResult,
  MembershipStaffSearchParams
> = {
  id: `person-${props.personId}-memberships`,
  rowKey: "assignId",
  resultMeta: MembershipStaffSearchResultMeta,
  searchMeta: MembershipStaffSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<MembershipStaffSearchParams>(
      MembershipStaffSearchParamsMeta,
    ),
    personIdExact: props.personId,
    purchasableStateExact: PurchasableState.ACTIVE,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchMembershipStaff(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
  visibleColumns: [
    "programName",
    "membershipTypeName",
    "assignId",
    "price",
    "purchasableState",
    "purchaseDate",
    "startDate",
    "expirationDate",
    "guestPrivileges",
  ],
  defaultSort: [{ key: "purchaseDate", order: "desc" }],
};
// Member search is scoped to the caller's household server-side; guest privileges are staff-only.
const memberConfig: GeneratedTableConfig<
  MembershipMemberSearchResult,
  MembershipMemberSearchParams
> = {
  ...staffConfig,
  id: `person-${props.personId}-memberships-member`,
  rowKey: "assignId",
  resultMeta: MembershipMemberSearchResultMeta,
  searchMeta: MembershipMemberSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<MembershipMemberSearchParams>(
      MembershipMemberSearchParamsMeta,
    ),
    personIdExact: props.personId,
    purchasableStateExact: PurchasableState.ACTIVE,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchMembershipMember(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
  visibleColumns: staffConfig.visibleColumns?.filter(
    (column) => column !== "guestPrivileges",
  ),
};
const isMember = props.mode === "member";
const config = (isMember ? memberConfig : staffConfig) as GeneratedTableConfig<
  Row,
  MembershipStaffSearchParams
>;
const headers = [
  {
    key: "price",
    title: "Price Paid",
    computeValue: (row: Row) => formatCost(row.price),
  },
  { key: "programName", title: "Program" },
  { key: "membershipTypeName", title: "Type" },
  {
    key: "purchaseDate",
    title: "Purchase Date",
    computeValue: (row: Row) => formatDateNY(row.purchaseDate),
  },
  {
    key: "startDate",
    title: "Start Date",
    computeValue: (row: Row) => formatDateNY(row.startDate),
  },
  {
    key: "expirationDate",
    title: "Expiration Date",
    computeValue: (row: Row) => formatDateNY(row.expirationDate),
  },
  ...(isMember
    ? []
    : [{ key: "guestPrivileges", title: "Guest Privileges", sortable: false }]),
];
const guestSlot = "item.guestPrivileges";
</script>

<template>
  <GeneratedPersonSearchTable
    :config="config"
    :hide-export="isMember"
    :headers="headers"
    :filter-keys="[
      'membershipTypeNameLike',
      'startDateGreaterEq',
      'expirationDateLessEq',
      'purchasableStateExact',
    ]"
    refresh-flag="membershipDataState"
  >
    <template v-slot:[guestSlot]="{ item }">
      <p v-if="'guestPrivileges' in item && item.guestPrivileges">Yes</p>
      <PersonAddGuestPrivsDialog
        v-else
        :existing-membership-id="item.assignId"
      />
    </template>
  </GeneratedPersonSearchTable>
</template>
